import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import PlannerChat from "@/components/PlannerChat";
import {
  ShieldCheck,
  MapPin,
  Heart,
  Sparkles,
  Mail,
  ArrowRight,
  Instagram,
  Phone,
  MessageCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { icon: ShieldCheck, title: "Vetted & Certified", body: "Every caregiver is background-checked, CPR / First Aid certified, and vetted through multi-stage interviews." },
  { icon: MapPin, title: "We Come To You", body: "White-glove childcare delivered to your event, hotel, villa, or corporate venue — anywhere in Los Angeles and beyond." },
  { icon: Heart, title: "Tailored Care Plans", body: "From infant care to toddler-focused educational play, every engagement is built around your child." },
];

const ComingSoon = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [launchIso, setLaunchIso] = useState("2026-07-10T19:00:00-07:00");
  const [openPlanner, setOpenPlanner] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/config`).then(({ data }) => {
      if (data?.launch_date_iso) setLaunchIso(data.launch_date_iso);
    }).catch(() => {});
  }, []);

  const target = useMemo(() => new Date(launchIso).getTime(), [launchIso]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  // When launch time arrives, exit the pre-launch page and reload into the live homepage.
  useEffect(() => {
    if (Number.isFinite(target) && Date.now() >= target) {
      // Small delay lets the visitor see the 00:00 flash for a beat.
      const t = setTimeout(() => { window.location.replace("/"); }, 1200);
      return () => clearTimeout(t);
    }
  }, [target, now]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !/^\S+@\S+\.\S+$/.test(email) || !phone.trim()) {
      toast.error("Please share your name, email, and phone.");
      return;
    }
    if (!consent) {
      toast.error("Please acknowledge the terms below.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, {
        full_name: name,
        email,
        phone,
        service_type: "other",
        event_date: new Date().toISOString().slice(0, 10),
        location: "TBD",
        children_ages: "TBD",
        number_of_children: 1,
        notes: `WAITLIST · Interested in: ${service || "(no preference)"}. Marketing opt-in: yes.`,
      });
      setDone(true);
      toast.success("You're on the list. We'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <main data-testid="coming-soon" className="min-h-screen bg-background text-foreground font-body">
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 pt-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground font-serif-display text-lg">C</span>
          <span className="font-serif-display text-xl md:text-2xl">Care Anywhere</span>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1">
            <Sparkles size={12} className="text-primary" /> Launching soon in Los Angeles
          </span>
        </div>
      </div>

      <section className="relative pt-16 pb-24 md:pt-24 md:pb-28">
        <div aria-hidden className="absolute -top-24 -right-24 h-[380px] w-[380px] rounded-full bg-secondary/50 blur-3xl opacity-70 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 backdrop-blur px-4 py-1.5 text-xs tracking-widest uppercase text-muted-foreground">
              <Sparkles size={12} className="text-primary" /> Coming soon
            </div>
            <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Premium childcare, <span className="italic text-primary">wherever</span> your family goes.
            </h1>
            <p className="mt-6 max-w-lg text-muted-foreground text-base md:text-lg leading-relaxed">
              Care Anywhere is building the most trusted mobile childcare service for events, travel, and elite families in Los Angeles. Our full booking platform launches shortly — join the waitlist to be the first to book.
            </p>

            {/* Countdown timer */}
            <div data-testid="countdown-timer" className="mt-8">
              <div className="text-[11px] tracking-[0.3em] uppercase text-primary/80 mb-3">Launching in</div>
              <div className="grid grid-cols-4 gap-2 max-w-md">
                {[{ v: days, l: "Days" }, { v: hours, l: "Hours" }, { v: mins, l: "Mins" }, { v: secs, l: "Secs" }].map(({ v, l }) => (
                  <div key={l} className="rounded-xl border border-border/70 bg-card px-2 py-3 text-center shadow-[0_10px_30px_-20px_rgba(63,109,138,0.35)]">
                    <div className="font-serif-display text-3xl md:text-4xl text-foreground tabular-nums">{String(v).padStart(2, "0")}</div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mt-1">{l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-muted-foreground">July 10, 2026 · 7:00 PM Pacific</div>
            </div>

            <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-primary" /> Los Angeles, CA</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck size={14} className="text-primary" /> CPR &amp; TrustLine certified</span>
            </div>

            <div className="mt-6">
              <Button
                data-testid="open-planner"
                type="button"
                variant="outline"
                onClick={() => setOpenPlanner(true)}
                className="rounded-full border-primary/30 hover:bg-primary/10"
              >
                <MessageCircle size={14} className="mr-1.5 text-primary" /> Chat with our AI Booking Concierge
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-border/70 bg-card p-6 md:p-8 shadow-[0_20px_60px_-30px_rgba(63,109,138,0.28)]">
            {done ? (
              <div data-testid="waitlist-success" className="text-center py-6">
                <div className="h-12 w-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <Mail size={20} className="text-primary" />
                </div>
                <h2 className="font-serif-display text-2xl">You're on the list.</h2>
                <p className="mt-2 text-sm text-muted-foreground">Our concierge team will reach out as soon as booking opens. In the meantime, follow us for launch updates.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="waitlist-form" className="space-y-4">
                <div>
                  <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Join the waitlist</div>
                  <div className="font-serif-display text-2xl mt-1">Be first to book.</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Full name</Label>
                  <Input data-testid="waitlist-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-lg" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                    <Input data-testid="waitlist-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-lg" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label>
                    <Input data-testid="waitlist-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-11 rounded-lg" placeholder="+1 555…" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">What kind of care interests you?</Label>
                  <Input data-testid="waitlist-service" value={service} onChange={(e) => setService(e.target.value)} placeholder="Event · Travel · Corporate · Family concierge" className="mt-1.5 h-11 rounded-lg" />
                </div>
                <div className="flex items-start gap-2 pt-1">
                  <Checkbox data-testid="waitlist-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-1" />
                  <label className="text-xs text-muted-foreground leading-relaxed">
                    I agree to Care Anywhere's <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link> and consent to receive service updates, promotional offers, and marketing emails. I can unsubscribe at any time.
                  </label>
                </div>
                <Button type="submit" data-testid="waitlist-submit" disabled={busy} className="w-full rounded-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground">
                  {busy ? "Adding you…" : (<>Join the waitlist <ArrowRight size={14} className="ml-1.5" /></>)}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 pb-24 grid grid-cols-1 md:grid-cols-3 gap-6">
        {FEATURES.map(({ icon: Icon, title, body }) => (
          <div key={title} className="rounded-2xl border border-border/70 bg-card p-7">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Icon size={20} className="text-primary" />
            </div>
            <h3 className="font-serif-display text-xl">{title}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
          </div>
        ))}
      </section>

      <footer className="bg-foreground text-background/90">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground font-serif-display">C</span>
              <span className="font-serif-display text-xl">Care Anywhere</span>
            </div>
            <p className="mt-4 text-sm text-background/70 max-w-sm">Premium mobile childcare, coming soon to Los Angeles and beyond.</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-background/60">Contact</div>
            <ul className="mt-4 space-y-2 text-sm text-background/80">
              <li className="flex items-center gap-2"><Mail size={13} className="text-primary" /> concierge.careanywhereagency@gmail.com</li>
              <li className="flex items-center gap-2"><Phone size={13} className="text-primary" /> (820) 386-2072</li>
              <li className="flex items-center gap-2"><Instagram size={13} className="text-primary" /> @careanywhere</li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-background/60">Legal</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/terms" data-testid="footer-terms" className="hover:text-primary transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-background/10 py-4 text-center text-xs text-background/50">
          © {new Date().getFullYear()} Care Anywhere · Los Angeles, CA
        </div>
      </footer>

      {openPlanner && <PlannerChat onClose={() => setOpenPlanner(false)} />}
    </main>
  );
};

export default ComingSoon;
